import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

const api = axios.create({
  baseURL: API_URL,
  // Generous timeout so a cold-starting server (e.g. waking from sleep) still
  // completes, but a genuinely dead request eventually fails gracefully
  // instead of hanging on a spinner forever.
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Token may live in localStorage ("remember me") or sessionStorage.
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const config = error.config || {};
    const isNetwork = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response;

    // Transient network/timeout errors are often just the server waking up
    // (e.g. a cold start). Quietly retry once before surfacing anything, so
    // users don't see a scary "can't connect" message for a brief blip.
    if (isNetwork && !config.__isRetry) {
      config.__isRetry = true;
      await new Promise((resolve) => setTimeout(resolve, 1200));
      try {
        return await api(config);
      } catch (retryErr) {
        error = retryErr;
      }
    }

    // Calm, non-alarming, non-technical messages. Never blame the user's
    // connection outright and never leak server internals.
    if (isNetwork) {
      error.userMessage = "We're having trouble reaching the server right now. Please try again in a moment.";
    } else if (error.response?.status === 500) {
      error.userMessage = 'Something went wrong on our side. Please try again shortly.';
    } else if (error.response?.status === 503) {
      error.userMessage = "We're just getting things ready. Please try again in a moment.";
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (email, password) => {
    return api.post('/auth/register', { email, password });
  },
  login: (email, password) => {
    return api.post('/auth/login', { email, password });
  },
  deleteAccount: (password) => {
    return api.delete('/auth/delete-account', { data: { password } });
  },
  deactivateAccount: (password) => {
    return api.post('/auth/deactivate-account', { password });
  },
  reactivateAccount: () => {
    return api.post('/auth/reactivate-account');
  },
  sendOTP: (phoneNumber) => {
    return api.post('/auth/phone/send-otp', { phoneNumber });
  },
  verifyRegistrationOTP: (userId, phoneNumber, otp) => {
    return api.post('/auth/verify-registration-otp', { userId, phoneNumber, otp });
  },
  sendEmailOTP: (userId) => {
    return api.post('/auth/email/send-otp', { userId });
  },
  verifyEmailOTP: (userId, otp) => {
    return api.post('/auth/email/verify-otp', { userId, otp });
  },
  requestEmailChange: (userId, newEmail) => {
    return api.post('/auth/change-email/request', { userId: userId.toString(), newEmail });
  },
  verifyEmailChange: (userId, code) => {
    return api.post('/auth/change-email/verify', { userId: userId.toString(), code });
  },
  getVerificationStatus: (userId) => {
    return api.get(`/auth/verification-status/${userId}`);
  },
};

export const profileAPI = {
  getMyProfile: () => api.get('/profiles/me'),
  getUserProfile: (userId) => api.get(`/profiles/user/${userId}`),
  updateProfile: (data) => api.post('/profiles/me', data),
  discoverProfiles: (page = 0, size = 10, maxDistance = null, availableForCall = null) => {
    let url = `/profiles/discover?page=${page}&size=${size}`;
    if (maxDistance) {
      url += `&maxDistance=${maxDistance}`;
    }
    if (availableForCall !== null) {
      url += `&availableForCall=${availableForCall}`;
    }
    return api.get(url);
  },
  updateActivity: () => api.post('/profiles/activity'),
  updateMood: (mood) => api.post('/profiles/mood', { mood }),
  toggleCallAvailability: (available, ratePerMinute = null) => {
    const payload = { available };
    if (ratePerMinute) {
      payload.ratePerMinute = ratePerMinute;
    }
    return api.post('/profiles/toggle-call-availability', payload);
  },
  getAvailableForCallCount: () => api.get('/profiles/available-for-call-count'),
};

export const feedAPI = {
  getFeed: (page = 0, size = 20) => api.get(`/feed?page=${page}&size=${size}`),
  getPosts: (page = 0, size = 20) => api.get(`/feed/posts?page=${page}&size=${size}`),
};

export const matchAPI = {
  likeUser: (userId) => api.post(`/likes/${userId}`),
  getMatches: () => api.get('/matches'),
};

export const messageAPI = {
  sendMessage: (receiverId, content, attachmentUrl = null, attachmentType = null) => {
    const payload = { receiverId, content };
    if (attachmentUrl) {
      payload.attachmentUrl = attachmentUrl;
      payload.attachmentType = attachmentType;
    }
    return api.post('/messages', payload);
  },
  getMessages: (conversationId, page = 0, size = 20) => 
    api.get(`/messages/conversation/${conversationId}?page=${page}&size=${size}`),
  deleteForMe: (messageId) => api.delete(`/messages/${messageId}/delete-for-me`),
  deleteForEveryone: (messageId) => api.delete(`/messages/${messageId}/delete-for-everyone`),
  editMessage: (messageId, content) => api.put(`/messages/${messageId}/edit`, { content }),
  uploadAttachment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/messages/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const conversationAPI = {
  getConversations: () => api.get('/conversations'),
  getOrCreateConversation: (otherUserId) => api.get(`/conversations/with/${otherUserId}`),
  unlockCreatorChat: (creatorId) => api.post(`/conversations/with/${creatorId}/unlock`),
  getUnreadCount: () => api.get('/conversations/unread-count'),
  contactSupport: () => api.get('/conversations/contact-support'),
};

export const blockAPI = {
  blockUser: (userId, reason) => api.post(`/block/${userId}`, { reason }),
  unblockUser: (userId) => api.delete(`/block/${userId}`),
  checkBlocked: (userId) => api.get(`/block/check/${userId}`),
  getBlockedUsers: () => api.get('/block/list'),
};

export const photoAPI = {
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/photos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadPrivatePhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/photos/upload-private', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadCoverPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/photos/upload-cover', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadCreatorContent: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/photos/upload-creator-content', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getMyPhotos: () => api.get('/photos/my-photos'),
  deletePhoto: (photoId) => api.delete(`/photos/${photoId}`),
  setProfilePicture: (photoId) => api.post(`/photos/${photoId}/set-primary`),
  canViewPrivate: () => api.get('/photos/can-view-private'),
  unlockPrivatePhotos: (profileOwnerId) => api.post(`/photos/unlock/${profileOwnerId}`),
  getUnlockStatus: (profileOwnerId) => api.get(`/photos/unlock-status/${profileOwnerId}`),
};

// PHASE 1: All messaging is FREE - credits system disabled
export const creditsAPI = {
  getBalance: () => api.get('/credits/balance'), // Still returns balance (shows unlimited)
  // PHASE 1: Payment disabled - messaging is free
  initiatePayment: (credits, amount) => {
    throw new Error('Payment is disabled in Phase 1. All messaging is now free!');
  },
  // Stripe payment disabled in Phase 1
  createStripeCheckout: (userId, credits, amount) => {
    throw new Error('Payment is disabled in Phase 1. All messaging is now free!');
  },
  verifyStripePayment: (sessionId) => {
    throw new Error('Payment is disabled in Phase 1. All messaging is now free!');
  },
};

// PHASE 1: Subscription tiers disabled - all messaging is free
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  // PHASE 1: Subscription tier purchases disabled
  initiateHeat: () => {
    throw new Error('Subscription tiers are disabled in Phase 1. All messaging is now free!');
  },
  initiateInferno: () => {
    throw new Error('Subscription tiers are disabled in Phase 1. All messaging is now free!');
  },
};

export const adminAPI = {
  getPendingCounts: () => api.get('/admin/pending-counts'),
};

export const reactionAPI = {
  addReaction: (messageId, reactionType) =>
    api.post(`/messages/${messageId}/reactions`, { reactionType }),
  removeReaction: (messageId, reactionType) =>
    api.delete(`/messages/${messageId}/reactions/${reactionType}`),
  getReactions: (messageId) =>
    api.get(`/messages/${messageId}/reactions`),
};

export const voucherAPI = {
  redeemVoucher: (code) => api.post('/vouchers/redeem', { code }),
  validateVoucher: (code) => api.get(`/vouchers/validate/${code}`),
  getMyRedemptions: () => api.get('/vouchers/my-redemptions'),
  // Admin endpoints
  createVoucher: (data) => api.post('/vouchers/admin/create', data),
  createVoucherBatch: (data) => api.post('/vouchers/admin/create-batch', data),
  getAllVouchers: () => api.get('/vouchers/admin/all'),
  getVouchersByStatus: (status) => api.get(`/vouchers/admin/status/${status}`),
  getVouchersByBatch: (batchId) => api.get(`/vouchers/admin/batch/${batchId}`),
  cancelVoucher: (code) => api.post(`/vouchers/admin/cancel/${code}`),
  getStatistics: () => api.get('/vouchers/admin/statistics'),
  expireOldVouchers: () => api.post('/vouchers/admin/expire-old'),
};

// Video calls are free and WhatsApp-style - receiver accepts or hangs up
export const videoCallAPI = {
  initiateCall: (receiverId) => api.post(`/video-calls/initiate/${receiverId}`),
  answerCall: (callId) => api.post(`/video-calls/${callId}/answer`),
  declineCall: (callId) => api.post(`/video-calls/${callId}/decline`),
  noAnswer: (callId) => api.post(`/video-calls/${callId}/no-answer`),
  endCall: (callId, reason = 'USER_HANGUP') => api.post(`/video-calls/${callId}/end?reason=${reason}`),
  getCallHistory: (page = 0, size = 20) => api.get(`/video-calls/history?page=${page}&size=${size}`),
  getMissedCalls: (page = 0, size = 20) => api.get(`/video-calls/missed?page=${page}&size=${size}`),
  clearMissedCalls: () => api.delete('/video-calls/missed'),
  getActiveCall: () => api.get('/video-calls/active'),
  getPendingCall: () => api.get('/video-calls/pending'),
  updatePeerInfo: (callId, peerInfo) => api.post(`/video-calls/${callId}/peer-info`, peerInfo),
  getPeerInfo: (callId, userId) => api.get(`/video-calls/${callId}/peer-info/${userId}`),
  getAgoraToken: (channelName, uid) => api.get(`/video-calls/agora-token?channelName=${channelName}&uid=${uid}`),
};

// PHASE 1: Creator subscriptions disabled - all content is free
export const creatorAPI = {
  enableCreatorMode: (subscriptionPrice, creatorBio) => 
    api.post('/creator/enable', { subscriptionPrice, creatorBio }),
  updateCreatorSettings: (subscriptionPrice, creatorBio) => 
    api.put('/creator/settings', { subscriptionPrice, creatorBio }),
  // PHASE 1: Creator subscriptions disabled - all content is free
  subscribeToCreator: (creatorId) => {
    throw new Error('Creator subscriptions are not available in Phase 1. All content is free!');
  },
  unsubscribeFromCreator: (creatorId) => {
    throw new Error('Creator subscriptions are not available in Phase 1. All content is free!');
  },
  getCreatorEarnings: () => api.get('/creator/earnings'),
  getUserSubscriptions: () => api.get('/creator/subscriptions'),
  isSubscribed: (creatorId) => api.get(`/creator/is-subscribed/${creatorId}`),
  
  // Content management
  uploadContent: (contentType, contentUrl, thumbnailUrl, caption, isExclusive) =>
    api.post('/creator/content/upload', { contentType, contentUrl, thumbnailUrl, caption, isExclusive }),
  getMyContent: () => api.get('/creator/content'),
  getCreatorContent: (creatorId) => api.get(`/creator/content/${creatorId}`),
  deleteContent: (contentId) => api.delete(`/creator/content/${contentId}`),
  updateContent: (contentId, caption) => api.put(`/creator/content/${contentId}`, { caption }),
  getSubscribers: () => api.get('/creator/subscribers'),
  
  // Notifications
  getNotifications: () => api.get('/creator/notifications'),
  getUnreadCount: () => api.get('/creator/notifications/unread-count'),
  markAsRead: (notificationId) => api.put(`/creator/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/creator/notifications/read-all'),
  
  // Content interactions
  trackView: (contentId) => api.post(`/creator/content/${contentId}/view`),
  toggleLike: (contentId) => api.post(`/creator/content/${contentId}/like`),
  
  // Comments
  addComment: (contentId, commentText) => 
    api.post(`/creator/content/${contentId}/comment`, { commentText }),
  getComments: (contentId) => api.get(`/creator/content/${contentId}/comments`),
  deleteComment: (commentId) => api.delete(`/creator/content/comment/${commentId}`),
  
  // Tips
  sendTip: (creatorId, amount) => api.post(`/creator/tip/${creatorId}`, { amount }),
  
  // Subscriptions management
  getMySubscriptions: () => api.get('/creator/my-subscriptions'),
  unsubscribe: (creatorId) => api.delete(`/creator/unsubscribe/${creatorId}`),
};

export const reportAPI = {
  reportUser: (userId, reason, description) => 
    api.post(`/reports/user/${userId}`, { reason, description }),
  getPendingCount: () => api.get('/reports/pending/count'),
};

export const onboardingAPI = {
  startOnboarding: () => api.post('/onboarding/start'),
  updateStep: (step) => api.post('/onboarding/step', { step }),
  completeOnboarding: () => api.post('/onboarding/complete'),
  getStatus: () => api.get('/onboarding/status'),
  getCompletionStatus: () => api.get('/onboarding/completion-status'),
  getNotifications: () => api.get('/onboarding/notifications'),
  markNotificationRead: (id) => api.post(`/onboarding/notifications/${id}/read`),
  dismissNotification: (id) => api.post(`/onboarding/notifications/${id}/dismiss`),
};

export const profileViewAPI = {
  recordView: (viewedUserId) => api.post('/profile-views/record', { viewedUserId }),
  getRecentViewers: (days = 7) => api.get(`/profile-views/recent?days=${days}`),
  getViewCount: (days = 7) => api.get(`/profile-views/count?days=${days}`),
  getMyProfileViews: () => api.get('/profile-views/my-views'),
};

export const payoutAPI = {
  requestPayout: (payoutData) => api.post('/payouts/request', payoutData),
  getPayoutHistory: () => api.get('/payouts/history'),
  getPayoutStats: () => api.get('/payouts/stats'),
  getPayoutById: (payoutId) => api.get(`/payouts/${payoutId}`),
  cancelPayout: (payoutId) => api.post(`/payouts/${payoutId}/cancel`),
};

export default api;
